import { create } from 'zustand';
import { fetchWithAuth, apiCall } from '@/services/api';
import { sanitizeConfigDto } from '@/services/configDto';

export type TenantStatus = 'pendente' | 'aprovado' | 'suspenso' | 'rejeitado' | 'cancelado';

export interface Tenant {
  id: string;
  slug: string;
  nome: string;
  descricao?: string;
  logo_url?: string;
  cor_primaria?: string;
  cor_secundaria?: string;
  status: TenantStatus;
  plano?: string;
  plano_expira_em?: string | null;
  trial_ativo?: boolean;
  feature_matcher_enabled?: boolean;
  limites_override?: any;
  trial_ate?: string;
  config?: any;
  created_at: string;
  updated_at?: string;
  qtd_lojas?: number;
  qtd_usuarios?: number;
  qtd_leads?: number;
  admin_email?: string;
  admin_nome?: string;
}

export interface TenantDetail {
  tenant: Tenant;
  lojas: Array<{ id: string; nome: string; endereco?: string }>;
  users: Array<{ id: string; nome: string; email: string; role: string; ativo: boolean }>;
  stats: {
    leads: number;
    messages: number;
    appointments: number;
  };
}

export interface AppUser {
  id: string;
  auth_user_id: string;
  tenant_id: string;
  loja_id?: string;
  lojas_ids?: string[];
  nome: string;
  email: string;
  role: string;
  ativo: boolean;
  created_at: string;
  tenant?: { id: string; nome: string; slug: string };
  loja?: { id: string; nome: string };
}

export interface GlobalMetrics {
  total_tenants: number;
  total_ativos: number;
  total_pendentes: number;
  total_usuarios: number;
  total_leads: number;
  total_mensagens: number;
  tenants_por_mes: Array<{ mes: string; total: number }>;
  leads_por_tenant: Array<{ tenant_id: string; nome: string; total: number }>;
  top_tenants: Array<{ tenant_id: string; nome: string; total: number }>;
}

export interface AuditLog {
  id: string;
  tenant_id?: string;
  actor_user_id?: string;
  target_user_id?: string;
  target_tenant_id?: string;
  action: string;
  metadata?: any;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  actor?: { id: string; nome: string; email: string };
  target_user?: { id: string; nome: string; email: string };
  target_tenant?: { id: string; nome: string; slug: string };
}

interface AdminState {
  tenants: Tenant[];
  tenantDetail: TenantDetail | null;
  users: AppUser[];
  globalMetrics: GlobalMetrics | null;
  auditLogs: AuditLog[];
  auditTotal: number;
  auditTotalPages: number;
  loading: boolean;

  fetchTenants: (silent?: boolean) => Promise<void>;
  fetchTenantDetail: (id: string) => Promise<void>;
  createTenant: (data: any) => Promise<{ success: boolean; error?: string }>;
  updateTenant: (id: string, data: Partial<Tenant>) => Promise<boolean>;
  approveTenant: (id: string) => Promise<boolean>;
  rejectTenant: (id: string, motivo: string) => Promise<boolean>;
  suspendTenant: (id: string) => Promise<boolean>;
  reactivateTenant: (id: string) => Promise<boolean>;
  cancelTenant: (id: string) => Promise<boolean>;
  deleteTenant: (id: string) => Promise<boolean>;
  updateTenantPlan: (id: string, planData: { plano?: string; limites_override?: any; expira_em?: string; trial_ativo?: boolean; feature_matcher_enabled?: boolean }) => Promise<{ success: boolean; error?: string }>;
  fetchAllUsers: (tenant_id?: string, silent?: boolean) => Promise<void>;
  createUser: (data: any) => Promise<{ success: boolean; error?: string }>;
  updateUser: (id: string, updates: { role?: string; ativo?: boolean; permissions?: any; lojas_ids?: string[] }) => Promise<boolean>;
  deleteUser: (id: string) => Promise<boolean>;
  resetPassword: (id: string) => Promise<{ success: boolean; new_password?: string; error?: string }>;
  fetchAuditLogs: (params?: { page?: number; limit?: number; action?: string; tenant_id?: string; actor_user_id?: string }) => Promise<void>;
  fetchGlobalMetrics: () => Promise<void>;
}

export const useAdminStore = create<AdminState>((set, get) => ({
  tenants: [],
  tenantDetail: null,
  users: [],
  globalMetrics: null,
  auditLogs: [],
  auditTotal: 0,
  auditTotalPages: 1,
  loading: false,

  fetchTenants: async (silent = false) => {
    if (!silent) set({ loading: true });
    try {
      const res = await apiCall('/api/admin/tenants');
      if (!res.ok) {
        throw new Error(`Falha ao carregar tenants: ${res.status}`);
      }
      const data = await res.json();
      set({ tenants: sanitizeConfigDto(data || []), loading: false });
      console.log('[ADMIN] tenants carregados:', data?.length);
    } catch (err) {
      console.error('[ADMIN_STORE] fetchTenants error:', err);
      set({ loading: false });
    }
  },

  fetchTenantDetail: async (id: string) => {
    try {
      const res = await fetchWithAuth(`/api/admin/tenants/${id}`);
      if (res.ok) {
        const data = await res.json();
        set({ tenantDetail: sanitizeConfigDto(data) });
      }
    } catch (err) {
      console.error('[ADMIN_STORE] fetchTenantDetail error:', err);
    }
  },

  updateTenant: async (id: string, data: Partial<Tenant>) => {
    const currentTenants = get().tenants;
    set({ tenants: currentTenants.map(t => t.id === id ? { ...t, ...data } : t) });
    try {
      const res = await fetchWithAuth(`/api/admin/tenants/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
      if (res.ok) {
        get().fetchTenants(true);
        return true;
      } else {
        set({ tenants: currentTenants });
      }
    } catch (err) {
      console.error('[ADMIN_STORE] updateTenant error:', err);
      set({ tenants: currentTenants });
    }
    return false;
  },

  approveTenant: async (id: string) => {
    const currentTenants = get().tenants;
    set({ tenants: currentTenants.map(t => t.id === id ? { ...t, status: 'aprovado' } : t) });
    try {
      const res = await fetchWithAuth(`/api/admin/tenants/${id}/approve`, {
        method: 'POST'
      });
      if (res.ok) {
        get().fetchTenants(true);
        return true;
      } else {
        set({ tenants: currentTenants });
      }
    } catch (err) {
      console.error('[ADMIN_STORE] approveTenant error:', err);
      set({ tenants: currentTenants });
    }
    return false;
  },

  rejectTenant: async (id: string, motivo: string) => {
    const currentTenants = get().tenants;
    set({ tenants: currentTenants.map(t => t.id === id ? { ...t, status: 'rejeitado' } : t) });
    try {
      const res = await fetchWithAuth(`/api/admin/tenants/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ motivo })
      });
      if (res.ok) {
        get().fetchTenants(true);
        return true;
      } else {
        set({ tenants: currentTenants });
      }
    } catch (err) {
      console.error('[ADMIN_STORE] rejectTenant error:', err);
      set({ tenants: currentTenants });
    }
    return false;
  },

  suspendTenant: async (id: string) => {
    const currentTenants = get().tenants;
    set({ tenants: currentTenants.map(t => t.id === id ? { ...t, status: 'suspenso' } : t) });
    try {
      const res = await fetchWithAuth(`/api/admin/tenants/${id}/suspend`, {
        method: 'POST'
      });
      if (res.ok) {
        get().fetchTenants(true);
        return true;
      } else {
        set({ tenants: currentTenants });
      }
    } catch (err) {
      console.error('[ADMIN_STORE] suspendTenant error:', err);
      set({ tenants: currentTenants });
    }
    return false;
  },

  reactivateTenant: async (id: string) => {
    const currentTenants = get().tenants;
    set({ tenants: currentTenants.map(t => t.id === id ? { ...t, status: 'aprovado' } : t) });
    try {
      const res = await fetchWithAuth(`/api/admin/tenants/${id}/reactivate`, {
        method: 'POST'
      });
      if (res.ok) {
        get().fetchTenants(true);
        return true;
      } else {
        set({ tenants: currentTenants });
      }
    } catch (err) {
      console.error('[ADMIN_STORE] reactivateTenant error:', err);
      set({ tenants: currentTenants });
    }
    return false;
  },

  cancelTenant: async (id: string) => {
    const currentTenants = get().tenants;
    set({ tenants: currentTenants.map(t => t.id === id ? { ...t, status: 'cancelado' } : t) });
    try {
      const res = await fetchWithAuth(`/api/admin/tenants/${id}/cancel`, {
        method: 'POST'
      });
      if (res.ok) {
        get().fetchTenants(true);
        return true;
      } else {
        set({ tenants: currentTenants });
      }
    } catch (err) {
      console.error('[ADMIN_STORE] cancelTenant error:', err);
      set({ tenants: currentTenants });
    }
    return false;
  },

  deleteTenant: async (id: string) => {
    const currentTenants = get().tenants;
    set({ tenants: currentTenants.filter(t => t.id !== id) });
    try {
      const res = await fetchWithAuth(`/api/admin/tenants/${id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        get().fetchTenants(true);
        return true;
      } else {
        set({ tenants: currentTenants });
      }
    } catch (err) {
      console.error('[ADMIN_STORE] deleteTenant error:', err);
      set({ tenants: currentTenants });
    }
    return false;
  },

  updateTenantPlan: async (id: string, planData: { plano?: string; limites_override?: any; expira_em?: string; trial_ativo?: boolean; feature_matcher_enabled?: boolean }) => {
    try {
      const res = await apiCall(`/api/admin/tenants/${id}/plano`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(planData)
      });
      if (res.ok) {
        const data = await res.json();
        set((state) => ({
          tenants: state.tenants.map((t) =>
            t.id === id
              ? {
                  ...t,
                  plano: data.tenant?.plano || planData.plano || t.plano,
                  plano_expira_em: data.tenant?.plano_expira_em,
                  limites_override: data.tenant?.limites_override,
                  trial_ativo: data.tenant?.trial_ativo,
                  feature_matcher_enabled: data.tenant?.feature_matcher_enabled !== undefined ? data.tenant.feature_matcher_enabled : t.feature_matcher_enabled
                }
              : t
          )
        }));
        return { success: true };
      } else {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || 'Erro ao atualizar plano' };
      }
    } catch (err: any) {
      return { success: false, error: err.message || 'Erro de conexão ao atualizar plano' };
    }
  },

  fetchAllUsers: async (tenant_id?: string) => {
    try {
      const query = tenant_id ? `?tenant_id=${tenant_id}` : '';
      const res = await apiCall(`/api/admin/users${query}`);
      if (!res.ok) {
        throw new Error(`Falha ao buscar usuários: ${res.status}`);
      }
      const data = await res.json();
      set({ users: data || [] });
      console.log('[ADMIN] usuários carregados:', data?.length);
    } catch (error) {
      console.error('[ADMIN] fetchAllUsers:', error);
    }
  },

  updateUser: async (id: string, updates: { role?: string; ativo?: boolean; permissions?: any; lojas_ids?: string[] }) => {
    try {
      const res = await fetchWithAuth(`/api/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        await get().fetchAllUsers();
        return true;
      }
    } catch (err) {
      console.error('[ADMIN_STORE] updateUser error:', err);
    }
    return false;
  },

  deleteUser: async (id: string) => {
    const currentUsers = get().users;
    set({ users: currentUsers.filter(u => u.id !== id) });
    try {
      const res = await fetchWithAuth(`/api/admin/users/${id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        await get().fetchAllUsers();
        return true;
      } else {
        set({ users: currentUsers });
      }
    } catch (err) {
      console.error('[ADMIN_STORE] deleteUser error:', err);
      set({ users: currentUsers });
    }
    return false;
  },

  createTenant: async (data: any) => {
    try {
      const res = await fetchWithAuth('/api/admin/tenants/create', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      const resData = await res.json();
      if (res.ok) {
        await get().fetchTenants();
        return { success: true };
      }
      return { success: false, error: resData.error || 'Erro ao criar empresa' };
    } catch (err: any) {
      console.error('[ADMIN_STORE] createTenant error:', err);
      return { success: false, error: err.message || 'Erro de conexão' };
    }
  },

  createUser: async (data: any) => {
    try {
      const res = await fetchWithAuth('/api/admin/users/create', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      const resData = await res.json();
      if (res.ok) {
        await get().fetchAllUsers();
        return { success: true };
      }
      return { success: false, error: resData.error || 'Erro ao criar usuário' };
    } catch (err: any) {
      console.error('[ADMIN_STORE] createUser error:', err);
      return { success: false, error: err.message || 'Erro de conexão' };
    }
  },

  resetPassword: async (id: string) => {
    try {
      const res = await fetchWithAuth(`/api/admin/users/${id}/reset-password`, {
        method: 'POST'
      });
      const data = await res.json();
      if (res.ok) {
        return { success: true, new_password: data.new_password };
      }
      return { success: false, error: data.error || 'Erro ao resetar senha' };
    } catch (err: any) {
      console.error('[ADMIN_STORE] resetPassword error:', err);
      return { success: false, error: err.message || 'Erro de conexão' };
    }
  },

  fetchAuditLogs: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.set('page', String(params.page));
      if (params.limit) queryParams.set('limit', String(params.limit));
      if (params.action) queryParams.set('action', params.action);
      if (params.tenant_id) queryParams.set('tenant_id', params.tenant_id);
      if (params.actor_user_id) queryParams.set('actor_user_id', params.actor_user_id);

      const res = await fetchWithAuth(`/api/admin/audit-logs?${queryParams.toString()}`);
      if (res.ok) {
        const data = await res.json();
        set({
          auditLogs: data.logs || [],
          auditTotal: data.total || 0,
          auditTotalPages: data.totalPages || 1
        });
      }
    } catch (err) {
      console.error('[ADMIN_STORE] fetchAuditLogs error:', err);
    }
  },

  fetchGlobalMetrics: async () => {
    try {
      const res = await fetchWithAuth('/api/admin/global-metrics');
      if (res.ok) {
        const data = await res.json();
        set({ globalMetrics: data });
      }
    } catch (err) {
      console.error('[ADMIN_STORE] fetchGlobalMetrics error:', err);
    }
  }
}));
